<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import QrCode from "svelte-qrcode";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { recordingStore } from "@stores/recordingStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { fly } from "svelte/transition";
  import {
    ToastNotification,
    Modal as DangerModal,
    Tag,
  } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  // import MultiSelect from "lib/components/MultiSelect.svelte";
  import * as XLSX from "xlsx";

  import Modal from "../../../../lib/components/Modal.svelte";
  import { formatISOTimestamp } from "../../../../lib/util/formatfunctions";
  import Header from "@components/Header.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import ImportTeamUsersModal from "../components/ImportTeamUsersModal.svelte";
  import TeamSubtaskAssignment from "../components/TeamSubtaskAssignment.svelte";
  import InstallationInstructions from "../InstallationInstructions.svelte";
  import {
    InfoBlock,
    SectionWrapper,
    TextInput,
    Select,
    SelectItem,
    Button,
    Tooltip,
  } from "paxray-components-svelte";
  import SearchInput from "../../settings/components/SearchInput.svelte";
  import { TRASH_ICON, EDIT_ICON } from "@utils/icons.js";

  export let isCompleted = false;

  const { getProjectLimitations, getProjectName } = projectListStore;
  const { getSubprocesses } = recordingStore;
  const urlParams = useUrlParams();

  const step = PROJECT_STAGES.SETUP.steps.TEAMS;
  const stage = PROJECT_STAGES.SETUP.id;

  let baseurl = "";
  let installerGenerated = isCompleted;
  let currentTeam = { damUsers: [], subprocesses: [] };
  let teams = [];
  let teamModalOpen = false;
  let teamImportModalOpen = false;
  let users = [];
  let subprocesses = [];
  let qrModalOpen = false;
  let installationInstructionsModalOpen = false;
  let projectName = "Project";
  let importConflictModalOpen = false;
  let importConflictReviewMode = false;
  let pendingImportPlan = null;
  let importConflictRows = [];
  let teamsPage = 1;
  let teamsPageSize = 10;
  let usersPage = 1;
  let usersPageSize = 10;
  let teamDataLoaded = false;
  let unassignedSubtaskCount = 0;
  let subtaskAssignmentsReady = false;
  let assignedSubtaskCounts = {};
  let usersSortKey = "lastupdate";
  let usersSortDirection = "descending";
  let showTeamGuide = false;
  let teamGuideStep = 0;
  let teamGuideSteps = [];

  let toastMsg;
  let saveSuccessful;

  let openDangerModal;
  let selectedOption = "all";
  let selectedDomain = "all";
  let teamSearchValue = "";
  let usersSearchValue = "";
  let listOfDomains = [];

  $: ({ idproject } = $urlParams);

  $: if (idproject) {
    assignedSubtaskCounts = {};
    loadProjectData();
  }

  $: projectName = $projectListStore.currentProjectName || "Project";

  $: {
    baseurl =
      window.location.protocol +
      "//" +
      window.location.host +
      (window.location.port ? ":" + window.location.port : "");
  }

  $: filteredUsers = users.filter((user) => {
    const normalizedSearch = usersSearchValue.trim().toLowerCase();

    let passesAssignmentFilter = true;
    if (selectedOption === "assigned") {
      passesAssignmentFilter = user.assigned !== "";
    } else if (selectedOption === "unassigned") {
      passesAssignmentFilter = user.assigned === "";
    }

    //Filter by domain
    let passesDomainFilter = true;
    if (selectedDomain !== "all") {
      passesDomainFilter = user.domain === selectedDomain;
    }

    let passesSearchFilter = true;
    if (normalizedSearch) {
      passesSearchFilter = [
        user.first_name,
        user.last_name,
        user.name,
        user.email,
        user.domain,
        user.assigned,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(normalizedSearch),
      );
    }

    return passesAssignmentFilter && passesDomainFilter && passesSearchFilter;
  });

  $: filteredTeams = teams.filter((team) => {
    const normalizedSearch = teamSearchValue.trim().toLowerCase();
    if (!normalizedSearch) return true;

    return [team.name, String(team.damUsers?.length ?? 0)].some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  });

  $: teamsWithTooFewUsers = teams.filter(
    (team) => (team.damUsers?.length || 0) < 3,
  );
  $: hasTeams = teams.length > 0;
  $: hasTeamWithTooFewUsers = teamsWithTooFewUsers.length > 0;
  $: teamsWithoutSubtasks = teams.filter(
    (team) => assignedSubtaskCounts[String(team.id)] === 0,
  );
  $: teamSetupIssues = [
    hasTeamWithTooFewUsers ? "Some teams have fewer than 3 members" : "",
    teamsWithoutSubtasks.length ? "Some teams have no assigned subtasks" : "",
  ].filter(Boolean);
  $: teamCountsLoaded =
    hasTeams &&
    teams.every((team) => assignedSubtaskCounts[String(team.id)] != null);
  $: if (
    teamsPage >
    Math.max(Math.ceil((filteredTeams || []).length / teamsPageSize), 1)
  ) {
    teamsPage = 1;
  }
  $: if (
    usersPage >
    Math.max(Math.ceil((filteredUsers || []).length / usersPageSize), 1)
  ) {
    usersPage = 1;
  }
  $: importConflictTableRows = importConflictRows.map((conflict, index) => ({
    id: String(index),
    conflictIndex: index,
    name: conflict.row.name,
    existingTeam: conflict.user.assigned,
    importedTeam: conflict.row.team,
    first_name: conflict.row.first_name,
    last_name: conflict.row.last_name,
    email: conflict.row.email,
    conflictType: [
      conflict.teamConflict ? "Team" : "",
      ...conflict.infoConflicts.map((item) => item.label),
    ]
      .filter(Boolean)
      .join(", "),
  }));

  function editTeam(id) {
    if (id != 0) {
      currentTeam = { ...teams.find((x) => x.id == id) };
    } else {
      currentTeam = {
        id: 0,
        typeIndex: 0,
        damUsers: [],
        subprocesses: [],
      };
    }
    selectedOption = "all";
    selectedDomain = "all";
    usersSortKey = "lastupdate";
    usersSortDirection = "descending";
    teamModalOpen = true;
  }

  function saveTeam() {
    let method = "POST";
    const teamToSave = {
      name: currentTeam.name,
      idproject: idproject,
      damUsers: currentTeam.damUsers,
      subprocesses: currentTeam.subprocesses,
    };

    if (currentTeam.id) {
      method = "PATCH";
      teamToSave.id = currentTeam.id;
    }

    fetchWithSession(
      `/api/damprojects/dam-teams${currentTeam.id ? "/" + currentTeam.id : ""}`,
      {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(teamToSave),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        saveSuccessful = data.ok;
        if (saveSuccessful) {
          if (data.data.id != currentTeam.id) {
            currentTeam.id = data.data.id;
            teams.push(currentTeam);
            teams = teams;
          } else {
            teams[teams.findIndex((x) => x.id == currentTeam.id)] = currentTeam;
          }
          updateUserAssignments();
        }
        teams = teams;
        teamModalOpen = false;
        toastMsg = data.message || data.error;
      })
      .catch((err) => {
        teamModalOpen = false;
        toastMsg = err.message;
      });
  }

  function deleteTeam() {
    fetchWithSession(`/api/damprojects/dam-teams/${currentTeam.id}`, {
      method: "DELETE",
    })
      .then((res) => res.json())
      .then((data) => {
        saveSuccessful = data.ok;
        if (saveSuccessful) {
          teams = teams.filter((x) => x.id != currentTeam.id);
          updateUserAssignments();
        }
        toastMsg = data.message || data.error;
      });

    openDangerModal = false;
  }

  async function handleLastUpdateClick(row) {
    try {
      const response = await fetchWithSession(`/api/settings/apikey`);
      const data = await response.json();
      const apiKey = data.apikey;
      window.open(
        `/data/getConfig?key=${apiKey}&username=${row.fullname}&version=0&checkuser=true`,
        "_blank",
      );
    } catch (err) {
      console.error("Error getting apikey:", err);
    }
  }

  async function getDamTeams() {
    return fetchWithSession(
      `/api/damprojects/dam-teams?idproject=${idproject}`,
      {
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        teams = data.map((team) => ({
          ...team,
          damUsers: team.damUsers.map((user) => user.id),
          subprocesses: team.subprocesses.map((subprocess) => subprocess.id),
        }));
      })
      .catch((err) => {
        console.error(err);
      });
  }

  function updateUserAssignments() {
    users = users
      .map((user) => {
        const assignedTeams = teams
          .filter((team) => team.damUsers.includes(user.id))
          .map((team) => team.name);

        return {
          ...user,
          assigned: assignedTeams.join(", ") || "",
        };
      })
      .sort((a, b) => {
        if (!a.assigned && !b.assigned) return 0;
        if (!a.assigned) return 1;
        if (!b.assigned) return -1;
        return a.assigned.localeCompare(b.assigned);
      });
  }

  function normalizeImportValue(value) {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }

  function displayCellValue(value) {
    const displayValue = String(value ?? "").trim();
    return displayValue || "-";
  }

  function sanitizeFilePart(value) {
    return (
      String(value || "Project")
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "") || "Project"
    );
  }

  function getDateStamp() {
    return new Date().toISOString().slice(0, 10);
  }

  function findExistingImportUser(row) {
    const importedName = normalizeImportValue(row.name);
    return users.find((user) =>
      [user.name, user.fullname].some(
        (value) => normalizeImportValue(value) === importedName,
      ),
    );
  }

  function getUserInfoConflicts(row, user) {
    return [
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email", label: "E-Mail" },
    ].filter(({ key }) => {
      const importedValue = normalizeImportValue(row[key]);
      const existingValue = normalizeImportValue(user[key]);
      return (
        importedValue &&
        existingValue &&
        existingValue !== "-" &&
        importedValue !== existingValue
      );
    });
  }

  function getImportConflicts(rows) {
    return rows
      .map((row, index) => {
        const user = findExistingImportUser(row);
        if (!user) return null;

        const assignedTeams = (user.assigned || "")
          .split(",")
          .map((team) => normalizeImportValue(team))
          .filter(Boolean);
        const teamConflict =
          assignedTeams.length > 0 &&
          !assignedTeams.includes(normalizeImportValue(row.team));
        const infoConflicts = getUserInfoConflicts(row, user);

        if (!teamConflict && !infoConflicts.length) return null;

        return {
          id: String(index),
          index,
          row,
          user,
          teamConflict,
          infoConflicts,
          acceptImported: true,
        };
      })
      .filter(Boolean);
  }

  async function createImportedTeams(teamNames) {
    const createdTeams = [];

    for (const teamName of teamNames) {
      const response = await fetchWithSession(`/api/damprojects/dam-teams`, {
        method: "POST",
        body: JSON.stringify({
          name: teamName,
          idproject: idproject,
          damUsers: [],
          subprocesses: [],
        }),
      });
      const data = await response.json();

      if (!data.ok) {
        throw new Error(
          data.message || data.error || `Error creating team ${teamName}`,
        );
      }

      createdTeams.push(data.data);
    }

    return createdTeams;
  }

  function buildImportPlan(rows) {
    const existingUserNames = new Set(
      users.flatMap((user) => [
        normalizeImportValue(user.name),
        normalizeImportValue(user.fullname),
      ]),
    );
    const existingTeamNames = new Set(
      teams.map((team) => normalizeImportValue(team.name)),
    );

    return {
      idproject,
      rows,
      createUsers: rows.filter(
        (row) => !existingUserNames.has(normalizeImportValue(row.name)),
      ),
      updateUsers: rows.filter((row) =>
        existingUserNames.has(normalizeImportValue(row.name)),
      ),
      createTeams: [
        ...new Set(
          rows
            .map((row) => row.team)
            .filter(
              (team) => !existingTeamNames.has(normalizeImportValue(team)),
            ),
        ),
      ],
    };
  }

  async function finalizeTeamImport(importPlan) {
    try {
      if (importPlan.createTeams.length) {
        await createImportedTeams(importPlan.createTeams);
        await getDamTeams();
      }

      console.log("Prepared team user import:", importPlan);
      teamImportModalOpen = false;
      importConflictModalOpen = false;
      importConflictReviewMode = false;
      pendingImportPlan = null;
      importConflictRows = [];
      saveSuccessful = true;
      toastMsg = `Import prepared: ${importPlan.rows.length} row(s) mapped. Created ${importPlan.createTeams.length} missing team(s). User save is not connected yet.`;
    } catch (err) {
      saveSuccessful = false;
      toastMsg = err.message;
    }
  }

  async function handleTeamImportSubmit(event) {
    const rows = (event.detail.rows || []).filter(
      (row) => row.team && row.name,
    );
    const importPlan = buildImportPlan(rows);
    const conflicts = getImportConflicts(rows);

    if (conflicts.length) {
      pendingImportPlan = importPlan;
      importConflictRows = conflicts;
      importConflictReviewMode = false;
      importConflictModalOpen = true;
      return;
    }

    await finalizeTeamImport(importPlan);
  }

  function applyAllImportConflicts() {
    if (pendingImportPlan) finalizeTeamImport(pendingImportPlan);
  }

  function downloadImportConflictRows() {
    const rows = importConflictRows.map((conflict) => ({
      Team: conflict.row.team,
      "First Name": conflict.row.first_name,
      "Last Name": conflict.row.last_name,
      "System Login Name": conflict.row.name,
      "E-Mail": conflict.row.email,
      "Existing Team": conflict.user.assigned,
      "Existing First Name": conflict.user.first_name,
      "Existing Last Name": conflict.user.last_name,
      "Existing E-Mail": conflict.user.email,
      Conflict: [
        conflict.teamConflict ? "Team" : "",
        ...conflict.infoConflicts.map((item) => item.label),
      ]
        .filter(Boolean)
        .join(", "),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Import Conflicts");
    XLSX.writeFile(
      workbook,
      `Import_Conflicts_Paxray_${sanitizeFilePart(projectName)}_${getDateStamp()}.xlsx`,
    );
  }

  function skipImportConflictsAndDownload() {
    if (!pendingImportPlan) return;

    downloadImportConflictRows();

    const ignoredIndexes = new Set(
      importConflictRows.map((conflict) => conflict.index),
    );
    const rowsWithoutConflicts = pendingImportPlan.rows.filter(
      (_, index) => !ignoredIndexes.has(index),
    );

    finalizeTeamImport(buildImportPlan(rowsWithoutConflicts));
  }

  function applyReviewedImportConflicts() {
    const ignoredIndexes = new Set(
      importConflictRows
        .filter((conflict) => !conflict.acceptImported)
        .map((conflict) => conflict.index),
    );
    const reviewedRows = pendingImportPlan.rows.filter(
      (_, index) => !ignoredIndexes.has(index),
    );

    finalizeTeamImport(buildImportPlan(reviewedRows));
  }

  async function loadProjectData() {
    teamDataLoaded = false;
    getProjectName(idproject);
    await getDamTeams();
    await loadUsers();
    await getSubprocesses();
    await loadProjectSubprocesses();
    await getProjectLimitations(idproject, stage);
    teamDataLoaded = true;
  }

  async function loadProjectSubprocesses() {
    try {
      const [projectResponse, departmentsResponse] = await Promise.all([
        fetchWithSession(`/api/damprojects/projects/${idproject}`),
        fetchWithSession("/api/damprojects/departments"),
      ]);
      const [project, departments] = await Promise.all([
        projectResponse.json(),
        departmentsResponse.json(),
      ]);
      const projectDepartmentIds = new Set(
        (project.departments || []).map((department) => department.id),
      );
      const projectProcessIds = new Set(
        (departments || [])
          .filter((department) => projectDepartmentIds.has(department.id))
          .flatMap((department) => department.processes || [])
          .map((process) => process.id),
      );

      subprocesses = ($recordingStore.subprocesses || []).filter((subprocess) =>
        projectProcessIds.has(subprocess.process_id),
      );
    } catch (err) {
      subprocesses = [];
      console.error("Error loading project subprocesses:", err);
    }
  }

  function loadUsers() {
    return fetchWithSession(`/api/damprojects/getDAMTeamUsers`)
      .then((res) => res.json())
      .then((data) => {
        const dataTimeFormated = data.map((item) => ({
          ...item,
          lastupdate: item.lastupdate
            ? formatISOTimestamp(item.lastupdate.slice(0, -6))
            : "-",
        }));
        const tempDomains = [];
        users = dataTimeFormated
          .map((user) => {
            const rawName = user.name || "";
            const [domain, ...nameParts] = rawName.split("\\");
            const name = nameParts.join("\\").replace(/^\\+/, "");
            if (domain) tempDomains.push(domain);
            const assignedTeam = teams
              .filter((team) => team.damUsers.includes(user.id))
              .map((team) => team.name);
            return {
              ...user,
              domain: domain || "-",
              first_name: user.first_name || user.firstName || "-",
              last_name: user.last_name || user.lastName || "-",
              name: name || rawName || "-",
              email: user.email || "-",
              assigned: assignedTeam.join(", ") || "",
              fullname: rawName,
            };
          })
          .sort((a, b) => {
            if (!a.assigned && !b.assigned) return 0;
            if (!a.assigned) return 1;
            if (!b.assigned) return -1;
            return a.assigned.localeCompare(b.assigned);
          });
        listOfDomains = [...new Set(tempDomains)];
      })
      .catch((err) => {
        console.error(err);
      });
  }

  onMount(() =>
    registerViewTour(() => {
      teamGuideSteps = [
        {
          target: '[data-guide="create-team"] button',
          title: "Create a new team",
          text: "Create a team and give it a clear name. In the team dialog, select at least three members. Use search and filters to find the people you need, then save your team.",
        },
        {
          target: '[data-guide="team-overview"] table',
          title: "Your project teams",
          text: "See your project teams, their member counts and assigned subtasks. Red badges indicate teams with one or two members or no assigned subtasks. Use the search above the table to find a team by name.",
        },
        {
          target:
            '[data-guide="team-overview"] .table-actions button[title="Edit"]',
          title: "Review and edit teams",
          text: "Use the pencil icon to open an existing team and review or update its member assignments.",
        },
        {
          target:
            '[data-guide="team-overview"] .table-actions button[title="Delete"]',
          title: "Delete a team",
          text: "Use the trash icon to remove a team you no longer need. You will be asked to confirm before the team is deleted.",
        },
        ...(hasTeams && subtaskAssignmentsReady
          ? [
              {
                target:
                  '[data-guide="team-settings"] .team-subtask-assignment table',
                title: "Assign subtasks to teams",
                text: "Assign the subtasks selected in your project framework to the responsible teams, individually or for an entire process. Every subtask needs at least one team. Any subtasks without a team are highlighted in red.",
              },
            ]
          : []),
        {
          target:
            '[data-guide="complete-team-settings"] > button, [data-guide="complete-team-settings"] > .completed-badge',
          title: "Complete and lock team settings",
          text: 'Review all teams and assignments before completing setup. Each team needs at least three members, and every selected subtask must have a team. Completing this step locks the settings and cannot be undone. Once this step is complete, the button is replaced by "Completed".',
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      teamGuideStep = 0;
      showTeamGuide = teamGuideSteps.length > 0;
    }),
  );

  $$restProps;
</script>

<Header
  section="Project"
  area="Setup"
  breadcrumb="Set Up Teams"
  tooltipText="Create project teams and assign users and project subtasks for data collection."
>
  <div data-guide="create-team">
    <Button
      kind="secondary"
      size="field"
      text="Create New Team"
      on:click={() => editTeam(0)}
    />
  </div>
  <Button
    kind="secondary"
    size="field"
    disabled={true}
    text="Import Teams (xlsx)"
    on:click={() => {
      // @adrian @alex @jonas: Need feedback on how to solve the team-user import backend-side
      // without breaking existing user creation, user metadata updates, and team assignments.
      return;
    }}
  />
  <div data-guide="complete-team-settings">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      bind:toastMsg
      bind:saveSuccessful
      allowAdminOverride={true}
      disabled={hasTeamWithTooFewUsers ||
        !subtaskAssignmentsReady ||
        unassignedSubtaskCount > 0}
      confirmBeforeComplete={true}
      text="Complete & Lock Team Settings"
      size="sm"
      completionWarning="After completion, the team settings cannot be changed. Please make sure all teams and assignments are correct before continuing. This action cannot be undone."
    />
  </div>
</Header>
<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main" data-guide="team-settings">
    <div class="team-overview" data-guide="team-overview">
      {#if teamDataLoaded}
        {#if !hasTeams}
          <InfoBlock
            type="info"
            heading="Team setup"
            message={'Create your teams, add at least 3 members to each and assign their subtasks. Then select "Complete & Lock Team Settings".'}
            showClose={false}
          />
        {:else}
          <InfoBlock
            type={teamSetupIssues.length
              ? "warning"
              : teamCountsLoaded
                ? "hint"
                : "info"}
            heading={teamSetupIssues.length
              ? teamSetupIssues.join(" • ")
              : teamCountsLoaded
                ? "All teams have at least 3 members and assigned subtasks."
                : "Checking team assignments…"}
            message={teamSetupIssues.length
              ? "Look for the red chips in the table below."
              : teamCountsLoaded
                ? "Review the subtask assignments below before completing setup."
                : "Subtask counts are loading."}
            showClose={false}
          />
        {/if}
      {/if}
      <DataTableBlack
        headers={[
          {
            key: "name",
            value: "Team",
            tooltip: "Name of the team used to group users for this project.",
          },
          {
            key: "users",
            value: "Members",
            tooltip: "Number of members currently assigned to this team.",
          },
          {
            key: "assignedSubtasks",
            value: "Assigned Subtasks",
            tooltip:
              "Number of project subtasks currently assigned to this team.",
          },
          { key: "menu", value: "Actions", width: "101px" },
        ]}
        rows={(filteredTeams || []).map((team) => ({
          ...team,
          assignedSubtasks: assignedSubtaskCounts[String(team.id)] ?? null,
        }))}
        totalItems={(filteredTeams || []).length}
        searchValue={teamSearchValue}
        showNewButton={false}
        showToolbar
        slicePage={true}
        bind:page={teamsPage}
        bind:pageSize={teamsPageSize}
        sortable
        on:search={(e) => {
          teamSearchValue = e.detail.value;
          teamsPage = 1;
        }}
        on:clearSearch={() => {
          teamSearchValue = "";
          teamsPage = 1;
        }}
      >
        <svelte:fragment slot="cell-header" let:header>
          {#if header.tooltip}
            <div class="header_with_tooltip">
              {header.value}
              <Tooltip>{header.tooltip}</Tooltip>
            </div>
          {:else}
            {header.value}
          {/if}
        </svelte:fragment>
        <svelte:fragment slot="cell" let:cell let:row>
          {#if cell.key === "menu"}
            <div class="table-actions">
              <Button
                icon={EDIT_ICON}
                size="small"
                iconDescription="Edit"
                kind="secondary"
                on:click={() => editTeam(row.id)}
              />
              {#if !installerGenerated}
                <Button
                  icon={TRASH_ICON}
                  size="small"
                  iconDescription="Delete"
                  kind="secondary"
                  on:click={() => {
                    currentTeam = row;
                    openDangerModal = true;
                  }}
                />
              {/if}
            </div>
          {:else if cell.key === "users"}
            {#if row.damUsers.length < 3}
              <Tag type="red" title="At least 3 members required"
                >{row.damUsers.length}</Tag
              >
            {:else}
              <Tag
                type="gray"
                style="background-color: var(--pax-smoke); color: var(--pax-black);"
                >{row.damUsers.length}</Tag
              >
            {/if}
          {:else if cell.key === "assignedSubtasks"}
            {#if cell.value === 0}
              <Tag type="red" title="No subtasks assigned">0</Tag>
            {:else if cell.value != null}
              <Tag
                type="gray"
                style="background-color: var(--pax-smoke); color: var(--pax-black);"
                >{cell.value}</Tag
              >
            {:else}
              —
            {/if}
          {:else}
            {cell.value}
          {/if}
        </svelte:fragment>
      </DataTableBlack>
    </div>
    {#if teamDataLoaded && idproject}
      {#key idproject}<TeamSubtaskAssignment
          projectId={idproject}
          {teams}
          bind:unassignedCount={unassignedSubtaskCount}
          bind:assignmentsReady={subtaskAssignmentsReady}
          bind:assignedSubtaskCounts
        />{/key}
    {/if}
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showTeamGuide}
  bind:currentStep={teamGuideStep}
  steps={teamGuideSteps}
  allowInteraction={false}
/>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:60px; right:20px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = null;
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<Modal
  autowidth
  bind:open={qrModalOpen}
  preventCloseOnClickOutside
  actions
  modalHeading="Workshop QR Code"
  primaryButtonText="Ok"
  secondaryButtonText="Cancel"
  on:click:button--secondary={() => {
    qrModalOpen = false;
  }}
  primaryButtonAction={() => {
    qrModalOpen = false;
  }}
  on:close={() => {
    qrModalOpen = false;
  }}
>
  <div style="margin:0 auto; width:300px;">
    <QrCode value={baseurl + "/UserInput?idproject=" + idproject} />
    <h4>{baseurl + "/UserInput?idproject=" + idproject}</h4>
  </div>
</Modal>

<div class="scoping-team-modal">
  <Modal
    autowidth
    hasScrollingContent={false}
    modalClass="team-settings-users-modal"
    preventCloseOnClickOutside
    bind:open={teamModalOpen}
    modalHeading="Team {currentTeam.name || ''}"
    on:open
    on:close={() => {
      teamModalOpen = false;
    }}
  >
    <div class="new_team_modal_wrapper">
      <div class="team-name-input">
        <TextInput
          labelText="Team name"
          placeholder="Enter team name"
          bind:value={currentTeam.name}
          readonly={installerGenerated}
          invalid={!currentTeam.name}
          invalidText="Please insert a name for the team"
        />
      </div>

      <div class="select_wrapper">
        <Select labelText="Assignment" bind:selected={selectedOption}>
          <SelectItem value="all" text="Show all" />
          <SelectItem value="assigned" text="Show assigned users" />
          <SelectItem value="unassigned" text="Show unassigned users" />
        </Select>

        <Select labelText="Domain" bind:selected={selectedDomain}>
          <SelectItem value="all" text="All domains" />
          {#each listOfDomains as domain}
            <SelectItem value={domain} text={domain} />
          {/each}
        </Select>

        <div class="user-search-control">
          <span class="user-filter-label">Search</span>
          <SearchInput
            placeholder="Search users..."
            on:search={(event) => {
              usersSearchValue = event.detail;
              usersPage = 1;
            }}
          />
        </div>
      </div>
      <div class="team-users-table">
        <DataTableBlack
          size="short"
          headers={[
            { key: "first_name", value: "First Name" },
            { key: "last_name", value: "Last Name" },
            { key: "name", value: "System Login Name" },
            { key: "email", value: "E-Mail" },
            { key: "domain", value: "Domain" },
            { key: "lastupdate", value: "Last Updated" },
            { key: "assigned", value: "Assigned team" },
          ]}
          rows={filteredUsers || []}
          totalItems={(filteredUsers || []).length}
          showToolbar={false}
          showNewButton={false}
          slicePage={true}
          pageSizes={[5, 10, 25, 50, 75, 100]}
          bind:page={usersPage}
          bind:pageSize={usersPageSize}
          bind:sortKey={usersSortKey}
          bind:sortDirection={usersSortDirection}
          bind:selectedRowIds={currentTeam.damUsers}
          sortable
          batchSelection
        >
          <svelte:fragment slot="cell-header" let:header>
            {#if header.key === "lastupdate"}
              <span class="last-update-header">{header.value}</span>
            {:else}
              {header.value}
            {/if}
          </svelte:fragment>
          <svelte:fragment slot="cell" let:cell let:row>
            {#if cell.key === "lastupdate"}
              <div class="last-update-cell">
                <button
                  type="button"
                  class="last-update-button"
                  on:click={() => handleLastUpdateClick(row)}
                >
                  {displayCellValue(cell.value)}
                </button>
              </div>
            {:else}
              {displayCellValue(cell.value)}
            {/if}
          </svelte:fragment>
        </DataTableBlack>
      </div>
      <!-- Legacy subprocess selection hidden in preparation for direct L4 assignments.
      <div class="subprocess-selector">
        <MultiSelect
          labelText="Subprocesses"
          label="Select subprocesses ..."
          direction="top"
          items={subprocesses.map((subprocess) => ({
            id: subprocess.id,
            text: subprocess.name,
          }))}
          bind:selectedIds={currentTeam.subprocesses}
        />
      </div>
      -->
    </div>
    <div class="team-modal-divider divider-margin" />
    <div class="actions-buttons-container">
      <Button
        kind="secondary"
        text="Cancel"
        on:click={() => {
          teamModalOpen = false;
        }}
      />
      <div class="primary-buttons">
        <Button
          kind="secondary"
          text="Help"
          on:click={() => {
            installationInstructionsModalOpen = true;
          }}
        />
        <Button
          text="Save"
          disabled={!currentTeam.name}
          on:click={() => {
            if (currentTeam.name) saveTeam();
          }}
        />
      </div>
    </div>
  </Modal>
</div>

<Modal
  autowidth
  hideFooter
  modalClass="installation-instructions-modal"
  modalHeading="Installation Instructions"
  bind:open={installationInstructionsModalOpen}
  on:close={() => {
    installationInstructionsModalOpen = false;
  }}
>
  <InstallationInstructions />
</Modal>

<ImportTeamUsersModal
  bind:open={teamImportModalOpen}
  {projectName}
  on:submit={handleTeamImportSubmit}
  on:close={() => {
    teamImportModalOpen = false;
  }}
/>

<Modal
  autowidth
  hideFooter
  modalClass="team-import-conflict-modal"
  preventCloseOnClickOutside
  bind:open={importConflictModalOpen}
  modalHeading="Import conflicts detected"
  on:close={() => {
    importConflictModalOpen = false;
    importConflictReviewMode = false;
  }}
>
  <div class="import-conflict-wrapper">
    {#if !importConflictReviewMode}
      <div class="copy-block">
        <h3>Some imported users already have existing information.</h3>
        <p>
          You can apply the imported values for all conflicts or review each row
          before continuing. Excluded conflicts will not be imported and will be
          downloaded as an Excel file for correction.
        </p>
      </div>

      <div class="conflict-table-wrapper">
        <DataTableBlack
          headers={[
            { key: "name", value: "System Login Name" },
            { key: "conflictType", value: "Conflict" },
            { key: "existingTeam", value: "Existing Team" },
            { key: "importedTeam", value: "Imported Team" },
            { key: "email", value: "Imported E-Mail" },
          ]}
          rows={importConflictTableRows}
          showToolbar={false}
          showPagination={false}
        >
          <svelte:fragment slot="cell" let:cell>
            {displayCellValue(cell.value)}
          </svelte:fragment>
        </DataTableBlack>
      </div>

      <div class="actions-buttons-container conflict-actions">
        <Button
          kind="secondary"
          text="Cancel"
          on:click={() => {
            importConflictModalOpen = false;
          }}
        />
        <Button
          kind="secondary"
          text="Exclude conflicts & download Excel"
          on:click={skipImportConflictsAndDownload}
        />
        <Button
          kind="secondary"
          text="Review individually"
          on:click={() => {
            importConflictReviewMode = true;
          }}
        />
        <Button text="Apply imported info" on:click={applyAllImportConflicts} />
      </div>
    {:else}
      <div class="copy-block">
        <h3>Review import conflicts</h3>
        <p>
          Keep the checkbox selected to use the imported row. You can adjust
          imported values before continuing. Excluded conflicts will not be
          imported and will be downloaded as an Excel file for correction.
        </p>
      </div>

      <div class="conflict-table-wrapper">
        <DataTableBlack
          headers={[
            { key: "acceptImported", value: "Use" },
            { key: "name", value: "System Login Name" },
            { key: "existingTeam", value: "Existing Team" },
            { key: "importedTeam", value: "Imported Team" },
            { key: "first_name", value: "First Name" },
            { key: "last_name", value: "Last Name" },
            { key: "email", value: "E-Mail" },
          ]}
          rows={importConflictTableRows}
          showToolbar={false}
          showPagination={false}
        >
          <svelte:fragment slot="cell" let:cell let:row>
            {#if cell.key === "acceptImported"}
              <input
                type="checkbox"
                checked={importConflictRows[row.conflictIndex].acceptImported}
                on:change={(event) => {
                  importConflictRows[row.conflictIndex].acceptImported =
                    event.currentTarget.checked;
                  importConflictRows = importConflictRows;
                }}
              />
            {:else if ["importedTeam", "first_name", "last_name", "email"].includes(cell.key)}
              <input
                value={cell.value}
                on:input={(event) => {
                  const key = cell.key === "importedTeam" ? "team" : cell.key;
                  importConflictRows[row.conflictIndex].row[key] =
                    event.currentTarget.value;
                  importConflictRows = importConflictRows;
                }}
              />
            {:else}
              {displayCellValue(cell.value)}
            {/if}
          </svelte:fragment>
        </DataTableBlack>
      </div>

      <div class="actions-buttons-container conflict-actions">
        <Button
          kind="secondary"
          text="Exclude conflicts & download Excel"
          on:click={skipImportConflictsAndDownload}
        />
        <Button
          kind="secondary"
          text="Back"
          on:click={() => {
            importConflictReviewMode = false;
          }}
        />
        <Button text="Continue" on:click={applyReviewedImportConflicts} />
      </div>
    {/if}
  </div>
</Modal>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="Delete"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={deleteTeam}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p class="deleteModalBody">
    Are you sure you want to delete this team:
    <span class="team-name">{currentTeam?.name}</span>?
    <span>This action cannot be undone.</span>
  </p>
</DangerModal>

<style>
  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .team-overview {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .main > div :global(.warning-notice) {
    margin-bottom: 0;
  }

  .header_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .table-actions {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4rem;
    white-space: nowrap;
  }

  .table-actions :global(.button_wrapper) {
    flex: 0 0 auto;
  }

  .new_team_modal_wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .team-users-table {
    margin-top: 1rem;
  }

  .select_wrapper {
    margin-top: 1rem;
  }

  :global(.team-name-input .bx--form-item) {
    width: 100%;
    margin-bottom: 0;
  }

  :global(.team-name-input .bx--label) {
    color: var(--pax-black);
  }

  :global(.team-name-input .bx--text-input) {
    height: 3rem;
    min-height: 3rem;
    padding: 0 1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background-color: var(--pax-white) !important;
    color: var(--pax-black);
  }

  :global(.team-name-input .bx--text-input:focus) {
    outline: 1px solid var(--pax-black);
    outline-offset: 0;
  }

  .team-name {
    font-weight: bold;
  }

  .select_wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(10rem, 1fr)) minmax(14rem, 2fr);
    gap: 1rem;
    align-items: end;
    width: 100%;
  }

  .select_wrapper :global(.bx--form-item),
  .select_wrapper :global(.bx--select),
  .select_wrapper :global(.bx--select-input__wrapper),
  .select_wrapper :global(.bx--select-input) {
    width: 100%;
  }

  .user-search-control {
    min-width: 0;
  }

  .user-filter-label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .user-search-control :global(.search-input) {
    box-sizing: border-box;
    width: 100%;
    height: 2.5rem;
  }

  .user-search-control :global(.search-input input) {
    width: 100%;
    min-width: 0;
  }

  :global(.subprocess-selector .bx--form-item) {
    margin-bottom: 0;
  }

  .team-modal-divider {
    width: 100%;
    height: 1px;
    background: #d9d9d9;
    margin-bottom: 1rem;
  }

  .last-update-header,
  .last-update-cell {
    display: flex;
    justify-content: center;
  }

  .last-update-button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
    text-align: center;
    text-decoration: underline;
  }

  .divider-margin {
    margin-top: 1rem;
  }

  .actions-buttons-container {
    display: flex;
    justify-content: space-between;
  }

  .actions-buttons-container .primary-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .copy-block h3 {
    margin: 0 0 0.5rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #161616;
  }

  .copy-block p {
    margin: 0;
    font-size: 0.8125rem;
    color: #6f6f6f;
  }

  .import-conflict-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: calc(96vw - 4rem);
  }

  .conflict-actions {
    gap: 0.5rem;
    justify-content: flex-end;
  }

  .conflict-table-wrapper {
    min-width: 56rem;
    max-height: 24rem;
    overflow: auto;
  }

  :global(.conflict-table-wrapper input[type="text"]),
  :global(.conflict-table-wrapper input:not([type])) {
    width: 100%;
    min-width: 8rem;
    height: 2.25rem;
    padding: 0 0.65rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.25rem;
    font: inherit;
  }

  :global(
      .custom-table.zebra-rows .bx--data-table tbody tr:nth-child(even) td
    ) {
    background-color: #f4f4f4 !important;
  }

  :global(.custom-table.zebra-rows .bx--data-table tbody tr:nth-child(odd) td) {
    background-color: #ffffff !important;
  }

  :global(.scoping-team-modal .modal_wrapper .divider),
  :global(.scoping-team-modal .actions-buttons-container) {
    display: none;
  }

  :global(.team-settings-users-modal .bx--modal-container) {
    width: auto !important;
    max-width: 96vw !important;
  }

  :global(.team-settings-users-modal .modal_wrapper) {
    width: 100% !important;
    max-width: 100%;
    min-width: 0;
  }

  :global(.team-settings-users-modal .new_team_modal_wrapper) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
  }

  :global(.team-settings-users-modal .bx--modal-scroll-content) {
    min-width: 0;
    overflow-x: hidden !important;
  }

  :global(.team-settings-users-modal .bx--modal-content) {
    overflow-y: hidden !important;
  }

  :global(.team-settings-users-modal .bx--modal-scroll-content > *:last-child) {
    padding-bottom: 0 !important;
  }

  :global(.team-import-conflict-modal .bx--modal-container) {
    width: auto !important;
    max-width: 96vw !important;
  }

  :global(.team-import-conflict-modal .modal_wrapper) {
    width: auto !important;
    max-width: calc(96vw - 2rem);
  }

  :global(.installation-instructions-modal .bx--modal-scroll-content) {
    overflow-x: hidden;
  }
</style>
